/**
 * Generation History panel — modal listing all AI-generated assets.
 * Filter by type, view prompts, re-apply to shots, save to disk.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { useAudioStore } from '@/store/audioStore.ts';
import { reapplyHistoryEntry, revertShotToLive } from '@/store/actions.ts';
import { capabilities } from '@/capabilities.ts';
import { enqueueSendToNative } from '@/bridge/embeddedBridge.ts';
import type { GenerationAssetType } from '@/types/generationHistory.ts';
import type { GenerationHistoryEntry } from '@/types/generationHistory.ts';

/** Extracts a frame from a video URL and displays it as a thumbnail image. */
function VideoThumb({ src, className }: { src: string; className: string }) {
  const [frameUrl, setFrameUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoked = false;
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.preload = 'auto';

    video.onloadeddata = () => {
      // Seek to 0.5s or 25% of duration (whichever is smaller) to skip black intro frames
      video.currentTime = Math.min(0.5, video.duration * 0.25);
    };

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0);
          const url = canvas.toDataURL('image/jpeg', 0.7);
          if (!revoked) setFrameUrl(url);
        }
      } catch { /* cross-origin or decode failure — leave blank */ }
      video.src = ''; // release memory
    };

    video.src = src;

    return () => { revoked = true; video.src = ''; };
  }, [src]);

  if (frameUrl) {
    return <img src={frameUrl} className={className} alt="" />;
  }
  // Fallback: play icon placeholder while frame loads
  return (
    <div className={className} style={{ background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="rgba(255,255,255,0.3)"><polygon points="6,4 20,12 6,20" /></svg>
    </div>
  );
}
import styles from './GenerationHistoryPanel.module.css';

type FilterType = 'all' | 'image' | 'video' | 'tts';

function timeAgo(iso: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return t('timeAgoJustNow');
  if (mins < 60) return t('timeAgoMinutes', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('timeAgoHours', { count: hrs });
  const days = Math.floor(hrs / 24);
  return t('timeAgoDays', { count: days });
}

function badgeClass(type: GenerationAssetType, metadata?: Record<string, unknown> | null): string {
  switch (type) {
    case 'image': return `${styles.badge} ${styles.badgeImage}`;
    case 'video':
    case 'transition_video': return `${styles.badge} ${styles.badgeVideo}`;
    case 'tts':
    case 'audio': {
      const lane = (metadata as Record<string, unknown> | undefined)?.lane;
      if (lane === 1) return `${styles.badge} ${styles.badgeMusic}`;
      if (lane === 2) return `${styles.badge} ${styles.badgeSfx}`;
      return `${styles.badge} ${styles.badgeTts}`;
    }
    default: return styles.badge;
  }
}

function badgeLabel(type: GenerationAssetType, t: (key: string) => string, metadata?: Record<string, unknown> | null): string {
  switch (type) {
    case 'image': return t('badgeImage');
    case 'video': return t('badgeVideo');
    case 'transition_video': return t('badgeTransition');
    case 'tts':
    case 'audio': {
      const lane = (metadata as Record<string, unknown> | undefined)?.lane;
      if (lane === 1) return t('badgeMusic');
      if (lane === 2) return t('badgeSfx');
      return t('badgeSpeech');
    }
    default: return type;
  }
}

function isVideoType(type: GenerationAssetType): boolean {
  return type === 'video' || type === 'transition_video';
}

function isAudioType(type: GenerationAssetType): boolean {
  return type === 'tts' || type === 'audio';
}

function fileExtension(mimeType: string): string {
  if (mimeType === 'video/mp4') return '.mp4';
  if (mimeType === 'video/webm') return '.webm';
  if (mimeType === 'image/png') return '.png';
  if (mimeType === 'audio/wav') return '.wav';
  if (mimeType === 'audio/mpeg' || mimeType === 'audio/mp3') return '.mp3';
  if (mimeType === 'audio/webm') return '.webm';
  if (mimeType.startsWith('audio/')) return '.wav';
  return '.jpg';
}

function fileSavePrefix(type: GenerationAssetType): string {
  switch (type) {
    case 'image': return 'enhanced';
    case 'tts': return 'speech';
    case 'audio': return 'audio';
    default: return 'video';
  }
}

/** Download a Blob URL as a file */
function downloadObjectUrl(objectUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** In embedded mode, ask Unity to extract the BLOB from SQLite and open the native share sheet */
function shareViaBridge(entry: GenerationHistoryEntry, filename: string) {
  enqueueSendToNative('shareAsset', {
    historyId: entry.id,
    mimeType: entry.mimeType,
    filename,
  });
}

/** In embedded mode, ask Unity to load and play an audio clip from generation history.
 *  Unity tries asset_data first, falls back to timeline_audioclips if empty. */
function playHistoryAudioViaBridge(historyId: string) {
  enqueueSendToNative('playHistoryAudio', { historyId });
}

function stopHistoryAudioViaBridge() {
  enqueueSendToNative('stopPreviewAudio', {});
}

/** Build replacement warnings for applying an entry to a specific shot */
function getReplacementWarnings(entry: GenerationHistoryEntry, shotId: number | null, t: (key: string, opts?: Record<string, unknown>) => string): string[] {
  if (!shotId) return [];
  const { project, videoShots } = useProjectStore.getState();
  if (!project) return [];

  const warnings: string[] = [];

  for (const scene of project.scenes) {
    const shot = scene.shots.find(s => s.id === shotId);
    if (!shot) continue;

    if (entry.assetType === 'image') {
      if (shot.historyId) {
        warnings.push(t('warningAiImageReplaced'));
      } else if (shot.thumbnailDataUrl) {
        warnings.push(t('warningThumbnailReplaced'));
      }
    } else if (entry.assetType === 'video') {
      if (videoShots.some(v => v.shotId === shotId)) {
        warnings.push(t('warningVideoReplaced'));
      }
    }

    // Warn if applying to a different shot than originally generated for
    if (entry.shotId && entry.shotId !== shotId) {
      const origScene = project.scenes.find(s => s.shots.some(sh => sh.id === entry.shotId));
      const origShot = origScene?.shots.find(sh => sh.id === entry.shotId);
      if (origScene && origShot) {
        const sep = project.documentSettings?.separator ?? '';
        warnings.push(t('warningOriginallyGeneratedFor', { shotNumber: `${origScene.number}${sep}${origShot.number}` }));
      }
    }
    break;
  }

  return warnings;
}

// ─── Action popover for a single entry ───────────────────────────────────

interface EntryPopoverProps {
  entry: GenerationHistoryEntry;
  shotLabel: string;
  selectedShotLabel: string | null;
  isApplied: boolean;
  initialConfirm?: boolean;
  onClose: () => void;
}

function EntryPopover({ entry, shotLabel, selectedShotLabel, isApplied, initialConfirm, onClose }: EntryPopoverProps) {
  const { t } = useTranslation('generationHistory');
  const selectedShotId = useProjectStore(s => s.selectedShotId);
  const readOnly = useProjectStore(s => s.readOnly);
  const [confirmWarnings, setConfirmWarnings] = useState<string[] | null>(
    initialConfirm ? getReplacementWarnings(entry, selectedShotId, t) : null
  );
  const [audioState, setAudioState] = useState<'idle' | 'loading' | 'playing' | 'error'>('idle');
  const [savingToDisk, setSavingToDisk] = useState(false);

  // Register callback for Unity to notify playback state changes; stop on unmount
  useEffect(() => {
    if (capabilities.embeddedAudio) {
      (window as unknown as Record<string, unknown>).__onPreviewStateChange = (state: string) => {
        if (state === 'stopped' || state === 'error') setAudioState(state === 'error' ? 'error' : 'idle');
        else if (state === 'playing') setAudioState('playing');
      };
    }
    return () => {
      if (capabilities.embeddedAudio) {
        stopHistoryAudioViaBridge();
        delete (window as unknown as Record<string, unknown>).__onPreviewStateChange;
      }
    };
  }, []);

  // No outside-click handler here — parent manages popover open/close
  // via handleEntryClick (switching entries) and handleListClick (dismiss).

  const handleSave = () => {
    if (!entry.objectUrl && !capabilities.bridge) return;
    const ext = fileExtension(entry.mimeType);
    const prefix = fileSavePrefix(entry.assetType);
    const name = `${prefix}-${shotLabel.replace(/\s/g, '-')}${ext}`;
    if (capabilities.bridge) {
      setSavingToDisk(true);
      // Unity calls window.__onShareReady() once the share sheet is presented
      (window as unknown as Record<string, unknown>).__onShareReady = () => {
        setSavingToDisk(false);
        delete (window as unknown as Record<string, unknown>).__onShareReady;
      };
      shareViaBridge(entry, name);
    } else {
      downloadObjectUrl(entry.objectUrl!, name);
      onClose();
    }
  };

  const handleApplyToShot = () => {
    if (readOnly) return;
    if (isAudioType(entry.assetType)) {
      reapplyHistoryEntry(entry.id, 0);
      onClose();
      return;
    }
    if (!selectedShotId) return;

    // Check for replacement warnings before applying
    const warnings = getReplacementWarnings(entry, selectedShotId, t);
    if (warnings.length > 0) {
      setConfirmWarnings(warnings);
      return;
    }

    reapplyHistoryEntry(entry.id, selectedShotId);
    onClose();
  };

  const handleConfirmReplace = () => {
    if (!selectedShotId) return;
    reapplyHistoryEntry(entry.id, selectedShotId);
    onClose();
  };

  const canApplyToShot = !readOnly && entry.objectUrl
    && !isAudioType(entry.assetType);
  const canAddToAudioLane = !readOnly && isAudioType(entry.assetType);

  const applyLabel = selectedShotLabel
    ? t('applyToShotLabel', { shotLabel: selectedShotLabel })
    : t('selectShotToApply');

  return (
    <div className={styles.entryPopover} onClick={e => e.stopPropagation()}>
      {/* Saving overlay */}
      {savingToDisk && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 10,
          background: 'rgba(0, 0, 0, 0.7)', borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 13, fontWeight: 500,
        }}>
          {t('preparingFile')}
        </div>
      )}
      {/* Preview */}
      <div className={styles.popoverPreview}>
        {!entry.objectUrl && !isAudioType(entry.assetType) ? (
          <div className={styles.prunedPreview}>{t('assetSavedToFile')}</div>
        ) : entry.mimeType.startsWith('image/') ? (
          <img src={entry.objectUrl} className={styles.popoverImg} alt="" />
        ) : isAudioType(entry.assetType) ? (
          <div className={styles.audioPreview}>
            {capabilities.embeddedAudio ? (
              <>
                <button
                  className={styles.audioPlayBtn}
                  disabled={audioState === 'loading'}
                  onClick={() => {
                    if (audioState === 'playing') {
                      stopHistoryAudioViaBridge();
                      setAudioState('idle');
                    } else {
                      setAudioState('loading');
                      playHistoryAudioViaBridge(entry.id);
                    }
                  }}
                >
                  {audioState === 'loading' ? (
                    <span style={{ fontSize: 11 }}>{t('loadingAudio')}</span>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      {audioState === 'playing'
                        ? <rect x="7" y="5" width="10" height="14" rx="1" />
                        : <polygon points="5 3 19 12 5 21 5 3" />
                      }
                    </svg>
                  )}
                </button>
                {audioState === 'error' && (
                  <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>{t('audioUnavailable')}</div>
                )}
              </>
            ) : entry.objectUrl ? (
              <audio src={entry.objectUrl} controls preload="metadata" />
            ) : (
              <div className={styles.prunedPreview}>{t('assetSavedToFile')}</div>
            )}
            {entry.metadata && (
              <div className={styles.audioMeta}>
                {(entry.metadata as Record<string, string>).character}
              </div>
            )}
          </div>
        ) : (
          <video src={entry.objectUrl} className={styles.popoverImg} controls muted playsInline preload="metadata" />
        )}
      </div>

      <div className={styles.popoverMeta}>
        <span className={badgeClass(entry.assetType, entry.metadata)}>{badgeLabel(entry.assetType, t, entry.metadata)}</span>
        {isApplied && <span className={styles.activeDot} title={t('currentlyAppliedTooltip')} />}
        <span className={styles.modelName}>{entry.modelId}</span>
      </div>

      <div className={styles.popoverPrompt}>{entry.prompt}</div>

      {confirmWarnings ? (
        <div className={styles.confirmSection}>
          {confirmWarnings.map((w, i) => (
            <p key={i} className={styles.confirmWarning}>{w}</p>
          ))}
          <div className={styles.popoverActions}>
            <button className={styles.popoverActionBtn} onClick={() => setConfirmWarnings(null)}>
              {t('cancelButton')}
            </button>
            <button className={`${styles.popoverActionBtn} ${styles.popoverActionBtnDanger}`} onClick={handleConfirmReplace}>
              {t('replaceButton')}
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.popoverActions}>
          <button className={styles.popoverActionBtn} onClick={handleSave} disabled={!entry.objectUrl}>
            {t('saveToDisk')}
          </button>
          {canApplyToShot && (
            <button className={styles.popoverActionBtn} onClick={handleApplyToShot} disabled={!selectedShotId}>
              {applyLabel}
            </button>
          )}
          {canAddToAudioLane && (
            <button className={styles.popoverActionBtn} onClick={handleApplyToShot} disabled={!entry.objectUrl}>
              {t('addToAudioLane')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main panel ──────────────────────────────────────────────────────────

interface PanelProps {
  /** Picker mode: clicking an entry calls onPick instead of showing popover */
  onPick?: (entry: GenerationHistoryEntry) => void;
  /** Custom close handler (defaults to toggleHistoryPanel from uiStore) */
  onDismiss?: () => void;
  /** Pre-set the filter tab */
  initialFilter?: FilterType;
}

export function GenerationHistoryPanel({ onPick, onDismiss, initialFilter }: PanelProps = {}) {
  const { t } = useTranslation('generationHistory');
  const toggleHistoryPanel = useUiStore(s => s.toggleHistoryPanel);
  const generationHistory = useAiStore(s => s.generationHistory);
  const project = useProjectStore(s => s.project);
  const selectedShotId = useProjectStore(s => s.selectedShotId);
  const readOnly = useProjectStore(s => s.readOnly);
  const isPicker = !!onPick;
  const handleClose = onDismiss ?? toggleHistoryPanel;

  const removeHistoryEntries = useAiStore(s => s.removeHistoryEntries);

  const [filter, setFilter] = useState<FilterType>(initialFilter ?? 'all');
  const [activePopoverId, setActivePopoverId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [showBulkDelete, setShowBulkDelete] = useState(false);

  // Build applied history ID set (shots whose historyId matches an entry)
  const appliedHistoryIds = useMemo(() => {
    const set = new Set<string>();
    if (!project) return set;
    for (const scene of project.scenes) {
      for (const shot of scene.shots) {
        if (shot.historyId) set.add(shot.historyId);
      }
    }
    return set;
  }, [project]);

  // Build full "used" set: shots + transitions + audio clips
  const usedHistoryIds = useMemo(() => {
    const ids = new Set(appliedHistoryIds);
    if (!project) return ids;
    // Transition videos
    for (const tr of project.transitions) {
      if (tr.videoHistoryId) ids.add(tr.videoHistoryId);
    }
    // TTS/audio entries linked to existing audio clips
    const clipIds = new Set(useAudioStore.getState().audioClips.map(c => c.id));
    for (const entry of generationHistory) {
      if (entry.clipId != null && clipIds.has(entry.clipId)) {
        ids.add(entry.id);
      }
    }
    return ids;
  }, [appliedHistoryIds, project, generationHistory]);

  // Build shot number lookup (uses separator from document settings)
  const shotNumberMap = useMemo(() => {
    const map = new Map<number, string>();
    if (!project) return map;
    const sep = project.documentSettings?.separator ?? '';
    for (const scene of project.scenes) {
      for (const shot of scene.shots) {
        map.set(shot.id, `${scene.number}${sep}${shot.number}`);
      }
    }
    return map;
  }, [project]);

  // Selected shot label for "Apply to Shot X-Y" button text
  const selectedShotLabel = useMemo(() => {
    if (!selectedShotId) return null;
    return shotNumberMap.has(selectedShotId) ? t('shotLabel', { number: shotNumberMap.get(selectedShotId) }) : null;
  }, [selectedShotId, shotNumberMap, t]);

  const handleQuickApply = useCallback((e: React.MouseEvent, entry: GenerationHistoryEntry) => {
    e.stopPropagation();
    if (readOnly || !entry.objectUrl) return;

    if (isAudioType(entry.assetType)) {
      reapplyHistoryEntry(entry.id, 0);
      return;
    }
    if (!selectedShotId) {
      import('@/components/Toast.tsx').then(({ showToast }) =>
        showToast(t('selectShotFirst'), 'error'));
      return;
    }

    const warnings = getReplacementWarnings(entry, selectedShotId, t);
    if (warnings.length > 0) {
      setActivePopoverId(entry.id);
      setConfirmingId(entry.id);
    } else {
      reapplyHistoryEntry(entry.id, selectedShotId);
    }
  }, [readOnly, selectedShotId, t]);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteConfirmId) return;
    // If this entry is applied to any shots, revert them to live
    if (project) {
      for (const scene of project.scenes) {
        for (const shot of scene.shots) {
          if (shot.historyId === deleteConfirmId) {
            revertShotToLive(shot.id);
          }
        }
      }
    }
    removeHistoryEntries([deleteConfirmId]);
    setDeleteConfirmId(null);
  }, [deleteConfirmId, project, generationHistory, removeHistoryEntries]);

  const handleBulkDeleteUnused = useCallback(() => {
    const unusedIds = generationHistory
      .filter(h => !usedHistoryIds.has(h.id))
      .map(h => h.id);
    if (unusedIds.length === 0) {
      import('@/components/Toast.tsx').then(({ showToast }) =>
        showToast(t('noUnusedAssets'), 'error'));
      setShowBulkDelete(false);
      return;
    }
    removeHistoryEntries(unusedIds);
    setShowBulkDelete(false);
  }, [generationHistory, usedHistoryIds, removeHistoryEntries, t]);

  const filtered = useMemo(() => {
    let entries: GenerationHistoryEntry[];
    if (filter === 'all') {
      entries = generationHistory;
    } else if (filter === 'video') {
      entries = generationHistory.filter(h => isVideoType(h.assetType));
    } else if (filter === 'tts') {
      entries = generationHistory.filter(h => isAudioType(h.assetType));
    } else {
      entries = generationHistory.filter(h => h.assetType === filter);
    }
    // Sort newest first
    return [...entries].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [generationHistory, filter]);

  const handleEntryClick = useCallback((id: string) => {
    if (isPicker) {
      const entry = generationHistory.find(h => h.id === id);
      if (entry && entry.objectUrl) {
        onPick!(entry);
      }
      return;
    }
    setActivePopoverId(prev => prev === id ? null : id);
  }, [isPicker, onPick, generationHistory]);

  const closePopover = useCallback(() => setActivePopoverId(null), []);

  // Close popover when tapping empty space in the list, header, filter bar, or footer
  const handlePanelClick = useCallback(() => {
    if (activePopoverId) setActivePopoverId(null);
  }, [activePopoverId]);

  const isImage = (mime: string) => mime.startsWith('image/');

  return (
    <div className={styles.backdrop} onClick={handleClose}>
      <div className={styles.panel} onClick={e => { e.stopPropagation(); handlePanelClick(); }}>
        <div className={styles.header}>
          <span className={styles.title}>{isPicker ? t('panelTitlePicker') : t('panelTitle')}</span>
          {!isPicker && !readOnly && (
            <button className={styles.headerTrashBtn} onClick={() => setShowBulkDelete(true)} title={t('deleteUnusedTooltip')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </button>
          )}
          <button className={styles.closeBtn} onClick={handleClose}>&times;</button>
        </div>

        <div className={styles.filterBar}>
          {(['all', 'image', 'video', 'tts'] as FilterType[]).map(f => (
            <button
              key={f}
              className={`${styles.filterTab} ${filter === f ? styles.filterTabActive : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? t('filterAll') : f === 'image' ? t('filterImages') : f === 'video' ? t('filterVideos') : t('filterAudio')}
            </button>
          ))}
        </div>

        <div className={styles.list}>
          {filtered.length === 0 && (
            <div className={styles.empty}>{t('noGenerationsYet')}</div>
          )}
          {filtered.map(entry => {
            const shotLabel = entry.shotId
              ? t('shotLabel', { number: shotNumberMap.get(entry.shotId) ?? entry.shotId })
              : t('detachedLabel');

            return (
              <div key={entry.id} className={styles.entryWrapper}>
                <div
                  className={`${styles.entry} ${activePopoverId === entry.id ? styles.entrySelected : ''}`}
                  onClick={e => { e.stopPropagation(); handleEntryClick(entry.id); }}
                >
                  {/* Thumbnail */}
                  {!entry.objectUrl ? (
                    <div className={`${styles.thumb} ${styles.prunedThumb}`} title={t('assetSavedToFile')} />
                  ) : isImage(entry.mimeType) ? (
                    <img src={entry.objectUrl} className={styles.thumb} alt="" />
                  ) : isAudioType(entry.assetType) ? (
                    <div className={`${styles.thumb} ${styles.audioThumb}`} title={t('speechClipTooltip')}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                      </svg>
                    </div>
                  ) : (
                    <div className={`${styles.thumb} ${styles.videoThumb}`}>
                      <VideoThumb src={entry.objectUrl} className={styles.thumb} />
                    </div>
                  )}

                  <div className={styles.entryInfo}>
                    <div className={styles.entryTop}>
                      <span className={badgeClass(entry.assetType, entry.metadata)}>{badgeLabel(entry.assetType, t, entry.metadata)}</span>
                      {appliedHistoryIds.has(entry.id) && <span className={styles.activeDot} title={t('currentlyAppliedTooltip')} />}
                      <span className={styles.modelName}>{entry.modelId}</span>
                    </div>
                    <div className={styles.prompt} title={entry.prompt}>{entry.prompt}</div>
                    <div className={styles.entryBottom}>
                      <span className={entry.shotId ? styles.shotLabel : `${styles.shotLabel} ${styles.detached}`}>
                        {shotLabel}
                      </span>
                      <span className={styles.timeAgo}>{timeAgo(entry.createdAt, t)}</span>
                    </div>
                  </div>

                  {/* Apply icon (hidden in picker mode) */}
                  {!isPicker && !readOnly && entry.objectUrl && (
                    <button
                      className={styles.applyIcon}
                      title={isAudioType(entry.assetType)
                        ? t('addToAudioLane')
                        : selectedShotLabel
                          ? t('applyToShotLabel', { shotLabel: selectedShotLabel })
                          : t('selectShotToApply')
                      }
                      disabled={!isAudioType(entry.assetType) && !selectedShotId}
                      onClick={e => handleQuickApply(e, entry)}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                        <polyline points="10 17 15 12 10 7" />
                        <line x1="15" y1="12" x2="3" y2="12" />
                      </svg>
                    </button>
                  )}

                  {/* Save icon (hidden in picker mode) */}
                  {!isPicker && (
                    <button
                      className={styles.saveIcon}
                      title={entry.objectUrl ? t('saveToDisk') : t('assetSavedToFile')}
                      disabled={!entry.objectUrl}
                      onClick={e => {
                        e.stopPropagation();
                        if (!entry.objectUrl) return;
                        const ext = fileExtension(entry.mimeType);
                        const prefix = fileSavePrefix(entry.assetType);
                        const name = `${prefix}-${shotLabel.replace(/\s/g, '-')}${ext}`;
                        if (capabilities.bridge) {
                          shareViaBridge(entry, name);
                        } else {
                          downloadObjectUrl(entry.objectUrl, name);
                        }
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </button>
                  )}

                  {/* Delete icon (hidden in picker and read-only modes) */}
                  {!isPicker && !readOnly && (
                    <button
                      className={styles.deleteIcon}
                      title={t('deleteEntryTooltip')}
                      onClick={e => {
                        e.stopPropagation();
                        setDeleteConfirmId(entry.id);
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                      </svg>
                    </button>
                  )}
                </div>

                {!isPicker && activePopoverId === entry.id && (
                  <EntryPopover
                    entry={entry}
                    shotLabel={shotLabel}
                    selectedShotLabel={selectedShotLabel}
                    isApplied={appliedHistoryIds.has(entry.id)}
                    initialConfirm={confirmingId === entry.id}
                    onClose={() => { closePopover(); setConfirmingId(null); }}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className={styles.footer}>
          {generationHistory.length !== 1
            ? t('generationCountPlural', { count: generationHistory.length })
            : t('generationCountSingular', { count: generationHistory.length })}
        </div>

        {/* Delete confirmation dialog */}
        {deleteConfirmId && (() => {
          const entry = generationHistory.find(h => h.id === deleteConfirmId);
          if (!entry) return null;
          const isApplied = appliedHistoryIds.has(entry.id);
          return (
            <div className={styles.confirmOverlay} onClick={() => setDeleteConfirmId(null)}>
              <div className={styles.confirmDialog} onClick={e => e.stopPropagation()}>
                <div className={styles.confirmTitle}>{t('deleteConfirmTitle')}</div>
                <div className={styles.confirmBody}>
                  {t('deleteConfirmBody', { type: badgeLabel(entry.assetType, t, entry.metadata).toLowerCase() })}
                </div>
                {isApplied && (
                  <div className={styles.confirmWarning}>{t('deleteConfirmAppliedWarning')}</div>
                )}
                <div className={styles.confirmActions}>
                  <button className={styles.popoverActionBtn} onClick={() => setDeleteConfirmId(null)}>
                    {t('cancelButton')}
                  </button>
                  <button className={`${styles.popoverActionBtn} ${styles.popoverActionBtnDanger}`} onClick={handleDeleteConfirm}>
                    {t('deleteButton')}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Bulk delete unused confirmation dialog */}
        {showBulkDelete && (
          <div className={styles.confirmOverlay} onClick={() => setShowBulkDelete(false)}>
            <div className={styles.confirmDialog} onClick={e => e.stopPropagation()}>
              <div className={styles.confirmTitle}>{t('bulkDeleteTitle')}</div>
              <div className={styles.confirmBody}>{t('bulkDeleteBody')}</div>
              <div className={styles.confirmActions}>
                <button className={styles.popoverActionBtn} onClick={() => setShowBulkDelete(false)}>
                  {t('cancelButton')}
                </button>
                <button className={`${styles.popoverActionBtn} ${styles.popoverActionBtnDanger}`} onClick={handleBulkDeleteUnused}>
                  {t('bulkDeleteConfirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
